/**
 * EmployeeDetailsPage
 * Page for viewing employee details — redesigned with data importance weights
 *
 * Importance tiers:
 *  T1 (Hero)   — Name, Status, Company/Dept/Position, CPF, Contact actions
 *  T2 (Stats)  — Birth date/age, Gender, Matrícula, Marital status
 *  T3 (Detail) — Full contact/address, Documents (CNH, CTPS, PIS), Personal (mother, birthplace)
 *  T4 (Cond.)  — PCD, Emergency contacts, CIPA, eSocial (only when data exists)
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Stack,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Edit,
  Business,
  Phone,
  Badge,
  Person,
  Email,
  WhatsApp,
  LocationOn,
  Work,
  Accessibility,
  Shield,
  ArrowBack,
  Description,
  Groups,
  Cake,
  Wc,
  FamilyRestroom,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useEmployee } from '../../hooks';
import { PageShell, IdentityBanner, InfoItem, SectionCard, StatCard } from '../../components';
import { EmployeeAvatar } from '../../components/employees/EmployeeAvatar';
import { EmployeeStatusChip } from '../../components/employees/EmployeeStatusChip';
import {
  formatCPF,
  formatPhone,
  formatCEP,
  formatDate,
  calculateAge,
  formatGender,
  formatMaritalStatus,
  formatTimeInService,
} from '../../utils/employeeFormatters';
import { SOCIAL_SECURITY_CLASSIFICATION_LABELS, SocialSecurityClassification, WORK_LOCATION_LABELS } from '../../types/employee';
import { ROTULO_DESLIGAMENTO, type TipoDeDesligamento } from '../../types/desligamento';
import { EmployeeESocialEventsCard } from './components/EmployeeESocialEventsCard';
import { EmployeeRisksCard } from './components/EmployeeRisksCard';
import { EmployeeExamsCard } from './components/EmployeeExamsCard';
import { EmployeeTrainingsCard } from './components/EmployeeTrainingsCard';
import { EmployeeMedicalLeavesCard } from './components/EmployeeMedicalLeavesCard';
import { EmployeeCatsCard } from './components/EmployeeCatsCard';
import { EmployeeAttendanceRiskCard } from './components/EmployeeAttendanceRiskCard';


/* ══════════════════════════════════════════════════════════════ */

export const EmployeeDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { employee, loading } = useEmployee(id ? parseInt(id) : undefined);

  const baseBreadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Funcionários', path: '/employees' },
  ];

  if (loading) {
    return (
      <PageShell
        breadcrumbs={[...baseBreadcrumbs, { label: 'Carregando...' }]}
        title="Funcionário"
      >
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!employee) {
    return (
      <PageShell
        breadcrumbs={[...baseBreadcrumbs, { label: 'Não encontrado' }]}
        title="Funcionário"
      >
        <Typography>Funcionário não encontrado</Typography>
      </PageShell>
    );
  }

  const hasDocuments =
    employee.cnh ||
    employee.documents?.ctps ||
    employee.documents?.pis;
  const hasPersonalExtra =
    employee.motherName || employee.birthplace || employee.maritalStatus;
  const hasEmergency =
    employee.emergencyContacts && employee.emergencyContacts.length > 0;
  const hasCipa = employee.cipa && employee.cipa.isMember;
  const hasEsocial =
    employee.socialSecurityClassification ||
    (employee.esocial &&
    (employee.esocial.inicioCondEsocial ||
      employee.esocial.motivo ||
      employee.esocial.gfip));
  const hasPCD = employee.hasDisability === 'Sim';

  return (
    <PageShell
      breadcrumbs={[...baseBreadcrumbs, { label: employee.name }]}
      actions={
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/employees')}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Edit />}
            onClick={() => navigate(`/employees/${id}/edit`)}
          >
            Editar
          </Button>
        </>
      }
    >
        {/* ═══ T1: IDENTIDADE ═══ */}
        <IdentityBanner
          avatar={
            <EmployeeAvatar
              name={employee.name}
              avatarUrl={null}
              size="large"
              sx={{ width: '100%', height: '100%', fontSize: '1.5rem' }}
            />
          }
          name={employee.name}
          status={<EmployeeStatusChip status={employee.status} />}
          context={
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              <Business sx={{ fontSize: 16 }} />
              <span>{employee.company?.tradeName || employee.company?.legalName || '—'}</span>
              <Typography component="span" color="text.disabled">›</Typography>
              <span>{employee.department?.name || '—'}</span>
              <Typography component="span" color="text.disabled">›</Typography>
              <Typography component="span" fontWeight={600} variant="body2">
                {employee.occupation?.name || '—'}
              </Typography>
              {employee.occupation?.cbo && (
                <Typography component="span" variant="caption" color="text.secondary">
                  CBO {employee.occupation.cbo}
                </Typography>
              )}
              {employee.socialName && (
                <Typography component="span" variant="body2" color="text.secondary">
                  · Nome Social: {employee.socialName}
                </Typography>
              )}
            </Box>
          }
          identifiers={
            <>
              <Tooltip title="Copiar CPF">
                <Chip
                  icon={<Badge sx={{ fontSize: 16 }} />}
                  label={formatCPF(employee.cpf)}
                  size="small"
                  variant="outlined"
                  onClick={() => navigator.clipboard.writeText(employee.cpf)}
                  sx={{ fontWeight: 600, fontFamily: 'monospace' }}
                />
              </Tooltip>

              {employee.employeeCode && (
                <Chip
                  icon={<Work sx={{ fontSize: 16 }} />}
                  label={`Mat. ${employee.employeeCode}`}
                  size="small"
                  variant="outlined"
                />
              )}

              {employee.whatsapp && (
                <Tooltip title={`WhatsApp: ${formatPhone(employee.whatsapp)}`}>
                  <IconButton
                    size="small"
                    color="success"
                    href={`https://wa.me/55${employee.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    sx={{
                      bgcolor: alpha('#25D366', 0.1),
                      '&:hover': { bgcolor: alpha('#25D366', 0.2) },
                    }}
                  >
                    <WhatsApp fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {employee.email && (
                <Tooltip title={employee.email}>
                  <IconButton
                    size="small"
                    color="primary"
                    href={`mailto:${employee.email}`}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                    }}
                  >
                    <Email fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          }
        />

        {/* ═══ T2: STAT CARDS ═══ */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<Cake fontSize="small" />}
              label="Data de Nascimento"
              value={formatDate(employee.birthDate)}
              sublabel={`${calculateAge(employee.birthDate)} anos`}
              color={theme.palette.info.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<Wc fontSize="small" />}
              label="Sexo"
              value={formatGender(employee.gender)}
              color={theme.palette.secondary.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<FamilyRestroom fontSize="small" />}
              label="Estado Civil"
              value={employee.maritalStatus ? formatMaritalStatus(employee.maritalStatus) : '—'}
              color="#8E24AA"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<Work fontSize="small" />}
              label="Admissão"
              value={employee.admissionDate ? formatDate(employee.admissionDate) : '—'}
              sublabel={employee.admissionDate ? formatTimeInService(employee.admissionDate) : undefined}
              color={theme.palette.success.main}
            />
          </Grid>
        </Grid>

        {/* ═══ T3: DETAIL SECTIONS ═══ */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {/* Contact & Address */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Contato & Endereço"
              icon={<Phone fontSize="small" />}
              accentColor={theme.palette.primary.main}
            >
              <Grid container spacing={2}>
                {employee.whatsapp && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      icon={<WhatsApp sx={{ fontSize: 14 }} />}
                      label="WhatsApp"
                      value={formatPhone(employee.whatsapp)}
                    />
                  </Grid>
                )}
                {employee.email && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      icon={<Email sx={{ fontSize: 14 }} />}
                      label="E-mail"
                      value={employee.email}
                    />
                  </Grid>
                )}
                {(employee.address || employee.city) && (
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <InfoItem
                      icon={<LocationOn sx={{ fontSize: 14 }} />}
                      label="Endereço"
                      fullWidth
                      value={
                        <>
                          {employee.address}
                          {employee.addressNumber && `, ${employee.addressNumber}`}
                          {employee.neighborhood && ` — ${employee.neighborhood}`}
                          <br />
                          {employee.city && employee.state && `${employee.city}/${employee.state}`}
                          {employee.zipCode && ` · CEP ${formatCEP(employee.zipCode)}`}
                        </>
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </SectionCard>
          </Grid>

          {/* Occupational Details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Dados Ocupacionais"
              icon={<Work fontSize="small" />}
              accentColor={theme.palette.success.main}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Empresa" value={employee.company?.tradeName || employee.company?.legalName} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Setor" value={employee.department?.name} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Cargo"
                    value={
                      <>
                        {employee.occupation?.name}
                        {(employee.cboCode || employee.occupation?.cbo) && (
                          <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
                            (CBO: {employee.cboCode || employee.occupation?.cbo})
                          </Typography>
                        )}
                      </>
                    }
                  />
                </Grid>
                {employee.employeeCode && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Matrícula" value={employee.employeeCode} />
                  </Grid>
                )}
                {employee.admissionDate && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Data de Admissão" value={formatDate(employee.admissionDate)} />
                  </Grid>
                )}
                {employee.dismissalDate && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Data de Demissão" value={formatDate(employee.dismissalDate)} />
                  </Grid>
                )}
                {employee.dismissalType && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Tipo de Desligamento"
                      value={ROTULO_DESLIGAMENTO[employee.dismissalType as TipoDeDesligamento] ?? employee.dismissalType}
                    />
                  </Grid>
                )}
                {employee.leaveDate && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Data de Afastamento" value={formatDate(employee.leaveDate)} />
                  </Grid>
                )}
                {employee.workLocation && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Tipo de Empresa" value={WORK_LOCATION_LABELS[employee.workLocation]} />
                  </Grid>
                )}
                {employee.unit?.name && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Unidade" value={employee.unit.name} />
                  </Grid>
                )}
                {employee.workRegime && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Regime de Trabalho" value={employee.workRegime} />
                  </Grid>
                )}
                {employee.cidCode && !hasPCD && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="CID" value={employee.cidCode} />
                  </Grid>
                )}
                {employee.disabilityDescription && !hasPCD && (
                  <Grid size={12}>
                    <InfoItem label="Descrição da Deficiência" value={employee.disabilityDescription} />
                  </Grid>
                )}
              </Grid>
            </SectionCard>
          </Grid>

          {/* Documents — only if any exist */}
          {hasDocuments && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Documentação"
                icon={<Description fontSize="small" />}
                accentColor="#FF8F00"
              >
                <Grid container spacing={2}>
                  {employee.cnh && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        label="CNH"
                        value={
                          <>
                            {employee.cnh}
                            {employee.cnhExpiration && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Validade: {formatDate(employee.cnhExpiration)}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </Grid>
                  )}
                  {employee.documents?.ctps && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        label="CTPS"
                        value={
                          <>
                            {employee.documents.ctps}
                            {employee.documents.serieCtps && ` · Série ${employee.documents.serieCtps}`}
                            {employee.documents.ufCtps && ` · ${employee.documents.ufCtps}`}
                          </>
                        }
                      />
                    </Grid>
                  )}
                  {employee.documents?.pis && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="PIS/NIS" value={employee.documents.pis} />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Personal extras — only if any exist */}
          {hasPersonalExtra && (
            <Grid size={{ xs: 12, md: hasDocuments ? 6 : 6 }}>
              <SectionCard
                title="Dados Pessoais"
                icon={<Person fontSize="small" />}
                accentColor="#546E7A"
              >
                <Grid container spacing={2}>
                  {employee.motherName && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Nome da Mãe" value={employee.motherName} />
                    </Grid>
                  )}
                  {employee.birthplace && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Naturalidade" value={employee.birthplace} />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}
        </Grid>

        {/* ═══ T3.5: OCCUPATIONAL SUMMARY CARDS ═══ */}
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Demonstrativos Ocupacionais
        </Typography>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <EmployeeESocialEventsCard employeeId={employee.id} companyId={employee.companyId} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <EmployeeRisksCard employeeId={employee.id} companyId={employee.companyId} departmentId={employee.departmentId} occupationId={employee.occupationId} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <EmployeeExamsCard employeeId={employee.id} companyId={employee.companyId} departmentId={employee.departmentId} occupationId={employee.occupationId} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <EmployeeTrainingsCard employeeId={employee.id} companyId={employee.companyId} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <EmployeeMedicalLeavesCard employeeId={employee.id} companyId={employee.companyId} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <EmployeeCatsCard employeeId={employee.id} companyId={employee.companyId} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <EmployeeAttendanceRiskCard employeeId={employee.id} companyId={employee.companyId} />
          </Grid>
        </Grid>

        {/* ═══ T4: CONDITIONAL SECTIONS ═══ */}
        {(hasPCD || hasEmergency || hasCipa || hasEsocial) && (
          <Grid container spacing={2.5}>
            {/* PCD */}
            {hasPCD && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                  title="PCD — Pessoa com Deficiência"
                  icon={<Accessibility fontSize="small" />}
                  accentColor="#E53935"
                >
                  <Grid container spacing={2}>
                    {employee.cidCode && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="CID" value={employee.cidCode} />
                      </Grid>
                    )}
                    {employee.disabilityDescription && (
                      <Grid size={12}>
                        <InfoItem label="Descrição da Deficiência" value={employee.disabilityDescription} />
                      </Grid>
                    )}
                  </Grid>
                </SectionCard>
              </Grid>
            )}

            {/* Emergency Contacts */}
            {hasEmergency && (
              <Grid size={12}>
                <SectionCard
                  title="Contatos de Emergência"
                  icon={<Groups fontSize="small" />}
                  accentColor="#D32F2F"
                >
                  <Stack spacing={1.5}>
                    {employee.emergencyContacts!.map((contact, index) => (
                      <Paper
                        key={contact.id || index}
                        variant="outlined"
                        sx={{ p: 2, bgcolor: alpha('#D32F2F', 0.02) }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" fontWeight={600}>
                                {contact.name}
                              </Typography>
                              {contact.isPrimary && (
                                <Chip label="Principal" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                              )}
                            </Box>
                            {contact.relationship && (
                              <Typography variant="caption" color="text.secondary">
                                {contact.relationship}
                              </Typography>
                            )}
                          </Grid>
                          <Grid size={{ xs: 12, sm: 3 }}>
                            <InfoItem
                              icon={<Phone sx={{ fontSize: 14 }} />}
                              label="Telefone"
                              value={formatPhone(contact.phone)}
                            />
                          </Grid>
                          {(contact.zipCode || contact.address || contact.city) && (
                            <Grid size={{ xs: 12, sm: 5 }}>
                              <InfoItem
                                icon={<LocationOn sx={{ fontSize: 14 }} />}
                                label="Endereço"
                                value={[
                                  contact.address && contact.addressNumber
                                    ? `${contact.address}, ${contact.addressNumber}`
                                    : contact.address,
                                  contact.complement,
                                  contact.neighborhood,
                                  contact.city && contact.state
                                    ? `${contact.city} - ${contact.state}`
                                    : contact.city,
                                  contact.zipCode && formatCEP(contact.zipCode),
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                </SectionCard>
              </Grid>
            )}

            {/* CIPA */}
            {hasCipa && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                  title="CIPA"
                  icon={<Shield fontSize="small" />}
                  accentColor="#1565C0"
                >
                  <Grid container spacing={2}>
                    {employee.cipa!.role && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Cargo na CIPA" value={employee.cipa!.role} />
                      </Grid>
                    )}
                    {employee.cipa!.term && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Mandato" value={employee.cipa!.term} />
                      </Grid>
                    )}
                    {employee.cipa!.startDate && (
                      <Grid size={12}>
                        <InfoItem
                          label="Período"
                          value={`${formatDate(employee.cipa!.startDate)}${employee.cipa!.endDate ? ` até ${formatDate(employee.cipa!.endDate)}` : ''}`}
                        />
                      </Grid>
                    )}
                    {employee.esocial?.estabilidade && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem
                          label="Estabilidade até"
                          value={formatDate(employee.esocial!.estabilidade)}
                        />
                      </Grid>
                    )}
                  </Grid>
                </SectionCard>
              </Grid>
            )}

            {/* eSocial */}
            {hasEsocial && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                  title="Dados eSocial"
                  icon={<Badge fontSize="small" />}
                  accentColor="#4527A0"
                >
                  <Grid container spacing={2}>
                    {employee.esocial?.inicioCondEsocial && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem
                          label="Início Condição"
                          value={formatDate(employee.esocial!.inicioCondEsocial)}
                        />
                      </Grid>
                    )}
                    {employee.esocial?.fimCondEsocial && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem
                          label="Fim Condição"
                          value={formatDate(employee.esocial!.fimCondEsocial)}
                        />
                      </Grid>
                    )}
                    {employee.esocial?.motivo && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Motivo" value={employee.esocial!.motivo} />
                      </Grid>
                    )}
                    {employee.esocial?.gfip && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="GFIP" value={employee.esocial!.gfip} />
                      </Grid>
                    )}
                    {employee.socialSecurityClassification && (
                      <Grid size={{ xs: 12 }}>
                        <InfoItem
                          label="Enquadramento Previdenciário"
                          value={SOCIAL_SECURITY_CLASSIFICATION_LABELS[employee.socialSecurityClassification as SocialSecurityClassification] || employee.socialSecurityClassification}
                        />
                      </Grid>
                    )}
                  </Grid>
                </SectionCard>
              </Grid>
            )}
          </Grid>
        )}
    </PageShell>
  );
};
